/**
 * Risk Level editor — Angular `ProductEditComponent` Risk Level tab (CS2NEW-1463).
 * Flat risk_labels list only (matrix / model picker removed from product edit).
 */
import type { ForwardedRef } from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Loader2, Lock, Unlock, Plus, X, ShieldCheck } from 'lucide-react'
import { SectionCard } from '@/components/ui/SectionCard'
import { showSuccess } from '@/lib/utils/notifications'
import type { ProductRiskLevelTabHandle, ProductRiskLevelTabProps, RiskLabel } from '../types'
import { fetchRiskLevel, saveRiskLevel } from '../services/productApi'
import {
  DEFAULT_RISK_COLORS,
  formatRiskLabelText,
  hasDuplicateRiskLevelNumbers,
  hexToLightBg,
  isRiskLevelNumberTaken,
  mapRiskLabel,
  normalizeRiskLabelText,
} from '../utils/productRiskMatrixModel'
import styles from '../styles/productRiskLevel.module.css'

function validateRiskLevelLabels(labels: RiskLabel[]): string | null {
  for (let i = 0; i < (labels || []).length; i++) {
    const label = labels[i]
    if (!label) continue
    const normalized = normalizeRiskLabelText(label.label)
    if (!normalized) {
      return `Risk label is required for level ${label.numeric_value}.`
    }
  }
  return null
}

export const ProductRiskLevelTab = forwardRef(function ProductRiskLevelTab(
  { productId, onError, onUiState }: ProductRiskLevelTabProps,
  ref: ForwardedRef<ProductRiskLevelTabHandle>,
) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [locked, setLocked] = useState(true)
  const [dirty, setDirty] = useState(false)
  const [labels, setLabels] = useState<RiskLabel[]>([])
  const [colorEditOnly, setColorEditOnly] = useState(false)
  const [formVersion, setFormVersion] = useState(0)

  const dirtyRef = useRef(dirty)
  const lockedRef = useRef(locked)
  const labelsRef = useRef(labels)
  dirtyRef.current = dirty
  lockedRef.current = locked
  labelsRef.current = labels

  useEffect(() => {
    onUiState?.({ dirty, locked })
  }, [dirty, locked, onUiState])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetchRiskLevel(productId)
      const raw = response.risk_labels || []
      setLabels(raw.map((lbl, idx) => mapRiskLabel(lbl, idx, '')))
      setColorEditOnly(!!response.color_edit_only)
      setDirty(false)
      setFormVersion((v) => v + 1)
    } catch (e) {
      onError((e as { message?: string }).message || 'Failed to load risk level')
    } finally {
      setLoading(false)
    }
  }, [productId, onError])

  useEffect(() => {
    void load()
  }, [load])

  const markDirty = () => setDirty(true)

  const updateLabel = (index: number, patch: Partial<RiskLabel>) => {
    setLabels((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)))
    markDirty()
  }

  const save = useCallback(async (): Promise<boolean> => {
    if (lockedRef.current) {
      onError('Risk Level is locked. Click Unlock to edit and save.')
      return false
    }
    const current = labelsRef.current
    const labelErr = validateRiskLevelLabels(current)
    if (labelErr) {
      onError(labelErr)
      return false
    }
    if (hasDuplicateRiskLevelNumbers(current)) {
      onError('Risk level numbers must be unique. Please fix duplicates before saving.')
      return false
    }

    const payloadLabels = current.map((l) => ({
      ...l,
      label: normalizeRiskLabelText(l.label),
    }))

    setSaving(true)
    try {
      await saveRiskLevel(productId, { risk_labels: payloadLabels })
      showSuccess('Risk level saved successfully!')
      setDirty(false)
      await load()
      return true
    } catch (e) {
      const msg = (e as { message?: string }).message || 'Failed to save risk level'
      onError(msg)
      if (msg.includes('already assigned to Applications')) {
        await load()
      }
      return false
    } finally {
      setSaving(false)
    }
  }, [productId, onError, load])

  useImperativeHandle(
    ref,
    () => ({
      save,
      cancel: () => {
        void load()
      },
      isDirty: () => dirtyRef.current,
      isLocked: () => lockedRef.current,
    }),
    [save, load],
  )

  const busy = loading || saving
  const fieldsDisabled = busy || locked
  const structureDisabled = fieldsDisabled || colorEditOnly

  return (
    <div data-testid="product-risk-level-tab" className="min-w-0">
      <SectionCard
        icon={ShieldCheck}
        title="Risk Level"
        testId="product-risk-level-section"
        titleTestId="product-risk-level-section-title"
        headerActions={
          <div className="flex flex-wrap items-start justify-end gap-3">
            <button
              type="button"
              data-testid="product-risk-level-lock-button"
              disabled={busy}
              onClick={() => setLocked((v) => !v)}
              className={
                locked
                  ? 'inline-flex h-[34px] items-center gap-2 rounded-full border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm'
                  : 'inline-flex h-[34px] items-center gap-2 rounded-full border border-emerald-400 bg-white px-3 text-sm font-semibold text-emerald-700 shadow-sm'
              }
            >
              {locked ? <Lock className="h-4 w-4" aria-hidden /> : <Unlock className="h-4 w-4" aria-hidden />}
              {locked ? 'Locked' : 'Unlocked'}
            </button>
            <div
              className="flex min-w-[160px] flex-col items-end gap-1.5 pt-1"
              aria-label="Risk level legend"
            >
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                {labels.map((lbl) => (
                  <span
                    key={`${lbl.numeric_value}-${lbl.color}`}
                    className="inline-flex h-[22px] w-[26px] items-center justify-center rounded-md border border-slate-900/10 text-xs font-bold"
                    style={{ backgroundColor: lbl.color, color: '#000' }}
                  >
                    {lbl.numeric_value}
                  </span>
                ))}
              </div>
              <span className="w-full text-right text-xs font-medium text-slate-500">Risk level</span>
            </div>
          </div>
        }
      >
        <p className="mb-4 text-sm text-slate-600">
          Define and manage risk levels and access fee percentages.
          {colorEditOnly
            ? ' Applications use this product. You can change the colors only.'
            : ''}
        </p>

        {loading ? (
          <div className="flex justify-center py-10 text-slate-500" data-testid="product-risk-level-loading">
            <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          </div>
        ) : (
          <div
            className="rounded-lg border border-slate-200 bg-slate-50/70 p-3"
            data-testid="product-risk-level-list"
          >
            <h3 className="mb-2 text-xs font-semibold text-slate-800">Risk levels</h3>
            <div className="flex flex-wrap items-center gap-3">
              {labels.map((lbl, index) => (
                <div
                  key={`${formVersion}-${index}-${lbl.id ?? 'new'}`}
                  className="flex shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                  data-testid={`product-risk-level-row-${index}`}
                >
                <input
                  type="number"
                  step={1}
                  defaultValue={lbl.numeric_value}
                  disabled={structureDisabled}
                  title="Numeric value (editable)"
                  className="h-[26px] w-11 rounded border px-1.5 text-center text-xs font-semibold"
                  style={{
                    backgroundColor: hexToLightBg(lbl.color),
                    color: '#000',
                    borderColor: lbl.color,
                  }}
                  onBlur={(e) => {
                    const newVal = parseInt(e.target.value, 10)
                    if (Number.isNaN(newVal)) {
                      e.target.value = String(lbl.numeric_value)
                      return
                    }
                    if (newVal === lbl.numeric_value) return
                    if (isRiskLevelNumberTaken(labelsRef.current, newVal, lbl)) {
                      onError('Risk level number must be unique (no duplicates).')
                      e.target.value = String(lbl.numeric_value)
                      return
                    }
                    updateLabel(index, { numeric_value: newVal })
                  }}
                  data-testid={`product-risk-level-row-${index}-numeric`}
                />
                <input
                  type="color"
                  value={lbl.color}
                  disabled={fieldsDisabled}
                  title={`Change color for level ${lbl.numeric_value}`}
                  className={styles.colorPicker}
                  onChange={(e) => updateLabel(index, { color: e.target.value })}
                  data-testid={`product-risk-level-row-${index}-color`}
                />
                <input
                  type="text"
                  defaultValue={lbl.label}
                  disabled={structureDisabled}
                  placeholder="Label"
                  title={`Label for level ${lbl.numeric_value}`}
                  className="h-[26px] w-[90px] rounded border border-slate-200 px-2 text-xs uppercase disabled:bg-slate-50"
                  onChange={(e) => {
                    const value = formatRiskLabelText(e.target.value, false)
                    e.target.value = value
                    updateLabel(index, { label: value })
                  }}
                  onBlur={(e) => {
                    const value = normalizeRiskLabelText(e.target.value)
                    e.target.value = value
                    updateLabel(index, { label: value })
                  }}
                  data-testid={`product-risk-level-row-${index}-label`}
                />
                <input
                  type="number"
                  defaultValue={lbl.access_fee_percentage}
                  disabled={structureDisabled}
                  placeholder="%"
                  title={`Access fee % for level ${lbl.numeric_value}`}
                  className="h-[26px] w-[52px] rounded border border-slate-200 px-1.5 text-center text-xs disabled:bg-slate-50"
                  onBlur={(e) => {
                    const num = parseFloat(e.target.value)
                    if (!Number.isNaN(num)) {
                      updateLabel(index, { access_fee_percentage: num })
                    }
                  }}
                  data-testid={`product-risk-level-row-${index}-pct`}
                />
                {labels.length > 1 ? (
                  <button
                    type="button"
                    disabled={structureDisabled}
                    title="Remove level"
                    className="inline-flex h-6 w-6 items-center justify-center rounded border-0 bg-transparent text-rose-500 hover:bg-rose-50 disabled:opacity-50"
                    onClick={() => {
                      if (labelsRef.current.length <= 1) return
                      setLabels((prev) => prev.filter((_, i) => i !== index))
                      markDirty()
                    }}
                    data-testid={`product-risk-level-row-${index}-remove`}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>
              ))}
              <button
                type="button"
                disabled={structureDisabled}
                title="Add risk level"
                className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md border border-dashed border-[rgb(21_151_255)] bg-white px-3 text-xs font-medium text-[rgb(21_151_255)] hover:bg-sky-50 disabled:opacity-50"
                onClick={() => {
                  const used = new Set(labelsRef.current.map((l) => Number(l.numeric_value)))
                  let newVal = 1
                  while (used.has(newVal)) newVal++
                  const colorIdx = labelsRef.current.length % DEFAULT_RISK_COLORS.length
                  setLabels((prev) => [
                    ...prev,
                    {
                      numeric_value: newVal,
                      label: '',
                      color: DEFAULT_RISK_COLORS[colorIdx],
                      access_fee_percentage: 0,
                    },
                  ])
                  markDirty()
                }}
                data-testid="product-risk-level-add-button"
              >
                <Plus className="h-4 w-4" aria-hidden />
                Add risk level
              </button>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  )
})

export type { ProductRiskLevelTabHandle } from '../types'
